import {
  useGetEscalationContactsQuery,
  useSetEscalationContactsMutation,
} from 'api/escalationContacts'
import { merchantUsersApi } from 'api/merchantUsers'
import {
  useGetAvailableRewardsQuery,
  useGetRecoveryConfigQuery,
  useUpdateRecoveryConfigMutation,
} from 'api/recoveryAi'
import { useEffect, useMemo } from 'react'
import {
  ESCALATION_CONTACTS_SAVE_KEY,
  ESCALATION_NOTIFY_SAVE_KEY,
} from './constants'

// Why Finalize is blocked, for the sr-only cue. Each state needs its own copy:
// a merchant mid-save or whose recipients failed to load has nothing to fix in
// the form, so "pick a recipient" would be the wrong instruction.
export type FinalizeBlockingReason =
  | 'loading' // a gate source has not settled
  | 'saving' // one of the stage's eager writes is in flight
  | 'save_failed' // an eager write rejected — the picker is ahead of the server
  | 'unavailable' // the contacts resource failed to load AND notify is off
  | 'recipient' // settled, and neither leg is satisfied

type ActivationCompleteness = {
  // True once the ONLY remaining Finalize requirement is met: a reachable human
  // on escalate. The reward leg was dropped from this verdict (see the function
  // doc below) — hasUsableReward is still computed and exposed separately.
  isComplete: boolean
  blockingReason: FinalizeBlockingReason | null
  // AI-884: the wizard's "approve rewards later" nudge was the only consumer and
  // is gone. Kept because it is the one place the live catalog is cross-checked
  // against the merchant's selection; deliberately NOT part of the verdict.
  hasUsableReward: boolean
  // The available-rewards catalog is still fetching. Scoped to hasUsableReward —
  // deliberately not part of the Finalize verdict, which has its own 'loading'
  // reason for the two sources that do gate it.
  isLoading: boolean
}

type Input = {
  // The caller's LIVE reward selection. No wizard step currently renders a
  // control to change this in-session; it is whatever the wizard hydrated once
  // from the saved config's approved_reward_ids (see useWizardHydration), or
  // empty for a merchant with no saved selection.
  selectedRewardIds: string[]
}

type Options = {
  // Skip the live refetches on callers/steps that don't evaluate the gate yet
  // (avoids a refetch on every wizard step entry). When skipped, hasUsableReward
  // is false and the verdict is not meaningful — the caller gates on isLastStep.
  skip?: boolean
}

/**
 * Single source for "is Finalize unlocked?" (D2, AI-173).
 *
 * The gate is satisfied when there is a reachable human on escalate: a PRIMARY
 * escalation recipient is selected, OR notify-location-managers is on. It reads
 * PERSISTED truth (the contacts resource + the saved config), never the step's
 * local state, so it can never diverge from what Settings shows.
 *
 *   context: master's wizard gated on a usable approved reward AND a valid
 *   free-text escalation email. #3683 dropped the Activation step and with it
 *   the gate (AI-173). The reward leg then came off by product decision
 *   (2026-08): a first-time merchant may activate with no approved rewards —
 *   thanx-ai's Bouncer treats an empty approved list as a DESIGNED terminal
 *   state and escalates every case to a human instead of silently doing
 *   nothing, so the agent can never go inert. The email leg came off with D1:
 *   escalation_emails is a field the notifier does not read. hasUsableReward is
 *   still computed (cross-checked against the live catalog, active-only) and
 *   returned separately, but as of AI-884 has no consumer — the wizard nudge it
 *   fed was removed.
 *
 * L17 — `notify_admins` is deliberately NOT a third leg. It defaults `true`
 * with no UI control anywhere, so OR-ing it in would make this gate vacuous.
 * Two legs is intentionally stricter than strictly necessary. Do not "fix" this.
 */
export function useActivationCompleteness(
  { selectedRewardIds }: Input,
  { skip = false }: Options = {}
): ActivationCompleteness {
  // Force a refetch of the live catalog (rewards can be deactivated from the
  // separate /rewards surface, which never invalidates this cache), but skip it
  // when the caller isn't evaluating the gate so we don't refetch every step.
  const { data: availableRewards, isFetching: rewardsFetching } =
    useGetAvailableRewardsQuery(undefined, {
      skip,
      refetchOnMountOrArgChange: true,
    })

  // NOT skipped: the wizard shell already holds an unskipped subscription
  // (Wizard/index.tsx) and so does useWizardHydration, so this costs no extra
  // request — and skipping would make "skipped" indistinguishable from "loaded
  // with notify off", which is the difference between a closed and an open gate.
  const { data: config, isLoading: configLoading } = useGetRecoveryConfigQuery()
  // Skipped off the last step — unlike the config, this IS a new subscription.
  const {
    data: contacts,
    isLoading: contactsLoading,
    isError: isContactsError,
  } = useGetEscalationContactsQuery(undefined, { skip })
  // The merchant-user roster, to check the saved primary still RESOLVES to
  // someone. Same `{}` arg the Escalations step's controller already subscribes
  // to, so on the step where this gate is evaluated it costs no extra request.
  const {
    data: usersData,
    isLoading: usersLoading,
    isError: isUsersError,
  } = merchantUsersApi.useGetMerchantUsersQuery({}, { skip })

  // Observe (never fire) the Escalations stage's two eager writes through their
  // shared cache keys. The step lives below this hook's caller, so there is no
  // other way to see a write it issued. Hole-destructured: the trigger belongs
  // to the step.
  const [
    ,
    {
      isLoading: isSavingContacts,
      isError: contactsSaveError,
      reset: resetContacts,
    },
  ] = useSetEscalationContactsMutation({
    fixedCacheKey: ESCALATION_CONTACTS_SAVE_KEY,
  })
  const [
    ,
    { isLoading: isSavingNotify, isError: notifySaveError, reset: resetNotify },
  ] = useUpdateRecoveryConfigMutation({
    fixedCacheKey: ESCALATION_NOTIFY_SAVE_KEY,
  })

  // The OBSERVER owns reset() under a shared fixedCacheKey (the shipped repo
  // pattern — see DashboardAccess/UsersTab and MerchantUsers/Landing). The step
  // must not reset on ITS unmount: stepping Back would clear an in-flight or
  // errored write out from under this gate. Cleared when the WIZARD unmounts.
  useEffect(
    () => () => {
      resetContacts()
      resetNotify()
    },
    [resetContacts, resetNotify]
  )

  // Pre-computed Set for the membership test (CLAUDE.md: no per-iteration array
  // scans inside .some()).
  const selectedIdSet = useMemo(
    () => new Set(selectedRewardIds),
    [selectedRewardIds]
  )

  // A usable reward = a live-selected reward still ACTIVE in the catalog (an
  // inactive reward can't be issued, so it never satisfies the gate).
  const hasUsableReward = useMemo(
    () =>
      (availableRewards ?? []).some(r => r.active && selectedIdSet.has(r.id)),
    [availableRewards, selectedIdSet]
  )

  // MONEY-SAFETY: `?? false` is load-bearing. The tempting fix for the
  // enable-flicker is a permissive default (`?? true`, or treating `undefined`
  // as satisfied) — that is the one change in this blast radius that would open
  // the gate BEFORE data lands, letting a merchant activate with no reachable
  // human (AI-173). Every naive version of this gate fails closed, so the only
  // visible symptoms of getting it wrong are a bad disabled-reason and a brief
  // enable-flicker. If a reviewer asks for the flicker to go away, add a
  // blockingReason; never loosen the default.
  const notifyManagers =
    config?.escalation_routing_notify_location_managers ?? false

  const knownUserIds = useMemo(
    () => new Set((usersData?.merchant_users ?? []).map(user => user.id)),
    [usersData]
  )
  // Same retained-data reasoning as contactsNeverLoaded below.
  const usersNeverLoaded = !!isUsersError && !usersData
  /**
   * Leg 1: a saved primary that still RESOLVES to a current merchant user.
   *
   *   context: the id alone is not proof of a reachable human. Deleting a merchant
   *   user destroys the membership link and leaves the escalation-contact row
   *   behind — no FK, no hook, no cleanup — so a stale id survives in the saved
   *   set. The controller and the picker both already drop an unresolvable id (the
   *   picker renders its placeholder), so accepting it here is the gate disagreeing
   *   with its own UI: Finalize enabled over a visibly empty recipient field, and
   *   the merchant's next edit silently PUTs the stale primary away.
   *
   * `usersNeverLoaded ||` is load-bearing in the OTHER direction. The persisted id
   * is truth; the roster is only a cross-check. merchant_users is gated on
   * `view merchant_users` — a DIFFERENT resource from the `manage recovery_ai` that
   * admits you to this wizard — so a custom role can 403 there while holding a
   * perfectly good recipient. Revoking on that would dead-end a merchant who can
   * activate today, and the 'unavailable' copy would point them at a toggle the
   * step does not render (it hard-errors on a users failure). So with no roster to
   * check against, keep today's answer.
   */
  const hasPrimaryRecipient =
    contacts?.primaryUserId != null &&
    (usersNeverLoaded || knownUserIds.has(contacts.primaryUserId))
  // RTK RETAINS last-good `data` across a failed REFETCH, so gate the
  // hard-unavailable branch on never-loaded, not on isError alone.
  const contactsNeverLoaded = !!isContactsError && !contacts

  // Early returns, no nested ternary. Leg 2 (notify) is checked FIRST on
  // purpose: it is config-sourced and the config is already subscribed by the
  // shell, so a merchant with notify saved ON never sees a contacts-driven
  // flicker — and a contacts 403/404 stays ESCAPABLE (flip the toggle) instead
  // of dead-ending the funnel.
  const resolveBlockingReason = (): FinalizeBlockingReason | null => {
    // ⚠ `&& !isSavingNotify` is load-bearing, not defensive noise. The eager
    //    notify flip is controller-local state, and updateRecoveryConfig patches
    //    the cache only on FULFILMENT — so while a notify-OFF write is in flight
    //    the cache still reads `true`. Without this clause that short-circuits
    //    past the two checks below and leaves Finalize ENABLED, letting the
    //    merchant activate with zero reachable humans inside the round-trip.
    //    It is asymmetric: toggling ON falls through correctly either way, which
    //    is why the hole survived review. DO NOT SIMPLIFY THIS BACK.
    if (notifyManagers && !isSavingNotify) return null
    if (contactsSaveError || notifySaveError) return 'save_failed'
    if (isSavingContacts || isSavingNotify) return 'saving'
    if (hasPrimaryRecipient) return null
    if (configLoading || contactsLoading || usersLoading) return 'loading'
    if (contactsNeverLoaded) return 'unavailable'
    return 'recipient'
  }

  // Fail-closed enumeration — twelve states, each traced through the ladder.
  // 'save_failed' deliberately outranks a persisted recipient: because the
  // contacts cache is patched only on success, hasPrimaryRecipient always
  // reflects persisted truth, so a merchant WITH a saved recipient is blocked by
  // a later unrelated failed write. Fail-closed, and Retry is on the step.
  const blockingReason = resolveBlockingReason()

  return {
    isComplete: blockingReason === null,
    blockingReason,
    hasUsableReward,
    isLoading: rewardsFetching,
  }
}
